import { keyBy, mapValues, range, every, capitalize, get } from 'lodash';
import createCachedSelector from 're-reselect';
import { CALL_API } from 'redux-api-middleware';

import { callGraphQLApi } from 'helpers/api';
import { reduceReducers, createReducer } from 'helpers/redux';
import paginate, { makeSelectors } from 'helpers/redux/paginate';
import { withConfirmation } from 'modules/confirmation';
import { getConfig } from 'modules/config';
export const namespace = 'engine-builds';
import { commonHeaders } from 'helpers/api';

export const LIST = 'vtn/engine-builds/LIST_BUILDS';
export const LIST_SUCCESS = 'vtn/engine-builds/LIST_BUILDS_SUCCESS';
export const LIST_FAILURE = 'vtn/engine-builds/LIST_BUILDS_FAILURE';

export const BUILD = 'vtn/engine-builds/BUILD';
export const BUILD_SUCCESS = 'vtn/engine-builds/BUILD_SUCCESS';
export const BUILD_FAILURE = 'vtn/engine-builds/BUILD_FAILURE';

export const DELETE = 'vtn/engine-builds/DELETE_BUILD';
export const DELETE_SUCCESS = 'vtn/engine-builds/DELETE_BUILD_SUCCESS';
export const DELETE_FAILURE = 'vtn/engine-builds/DELETE_BUILD_FAILURE';

export const BUILD_ACTION = 'vtn/engine-builds/BUILD_ACTION';
export const BUILD_ACTION_SUCCESS = 'vtn/engine-builds/BUILD_ACTION_SUCCESS';
export const BUILD_ACTION_FAILURE = 'vtn/engine-builds/BUILD_ACTION_FAILURE';

export const DOWNLOAD_BUILD_REPORT = 'vtn/engine-builds/DOWNLOAD_BUILD_REPORT';

export const BUILD_NODE_RED_ENGINE = 'vtn/engine-builds/BUILD_NODE_RED_ENGINE';

export const FETCH_WORKFLOW_RUNTIME_DATA =
  'vtn/engine/FETCH_WORKFLOW_RUNTIME_DATA';
export const FETCH_WORKFLOW_RUNTIME_DATA_SUCCESS =
  'vtn/engine/FETCH_WORKFLOW_RUNTIME_DATA_SUCCESS';
export const FETCH_WORKFLOW_RUNTIME_DATA_FAILURE =
  'vtn/engine/FETCH_WORKFLOW_RUNTIME_DATA_FAILURE';

export const UPDATE_WORLFLOW_DATA = 'vtn/engine-builds/UPDATE_WORLFLOW_DATA';
export const UPDATE_WORLFLOW_DATA_SUCCESS =
  'vtn/engine-builds/UPDATE_WORLFLOW_DATA_SUCCESS';
export const UPDATE_WORLFLOW_DATA_FAILURE =
  'vtn/engine-builds/UPDATE_WORLFLOW_DATA_FAILURE';

export const REDIRECT_LATEST_AUTOMATE_BUILD =
  'vtn/engine-builds/REDIRECT_LATEST_AUTOMATE_BUILD';

export const CREATE_NEW_ENGINE_BUILD =
  'vtn/engine-builds/CREATE_NEW_ENGINE_BUILD';
export const CREATE_NEW_ENGINE_BUILD_SUCCESS =
  'vtn/engine-builds/CREATE_NEW_ENGINE_BUILD_SUCCESS';
export const CREATE_NEW_ENGINE_BUILD_FAILURE =
  'vtn/engine-builds/CREATE_NEW_ENGINE_BUILD_FAILURE';

const defaultState = {
  buildsById: {},
  createBuildForm: {
    dockerImageUrl: '',
    loading: false,
  },
};

const reducer = reduceReducers(
  paginate({
    types: [LIST, LIST_SUCCESS, LIST_FAILURE],
    invalidateOnTypes: [DELETE_SUCCESS],
    idAttribute: 'id',
    keyBy: 'engineId',
    selectResponseResults: (action) => {
      return get(action.payload, 'data.engine.builds.records', []);
    },
    selectResponseTotal: (action) => {
      return get(action.payload, 'data.engine.totalBuilds.count', 0);
    },
  }),
  createReducer(defaultState, {
    [LIST_SUCCESS](state, action) {
      return {
        ...state,
        buildsById: {
          ...state.buildsById,
          ...keyBy(get(action.payload, 'data.engine.builds.records'), 'id'),
        },
      };
    },
    [BUILD_ACTION_SUCCESS](state, action) {
      return {
        ...state,
        buildsById: {
          ...state.buildsById,
          [action.meta.input.id]: get(action.payload, 'data.updateEngineBuild'),
        },
      };
    },
    [BUILD_SUCCESS](state, action) {
      return {
        ...state,
        buildID: action,
      };
    },
    [CREATE_NEW_ENGINE_BUILD](state, action) {
      const dockerImageUrl = get(action.meta, 'input.dockerImage');
      return {
        ...state,
        createBuildForm: {
          ...state.createBuildForm,
          loading: true,
          dockerImageUrl: dockerImageUrl,
        },
      };
    },
    [CREATE_NEW_ENGINE_BUILD_SUCCESS](state) {
      return {
        ...state,
        createBuildForm: { loading: false, dockerImageUrl: '' },
      };
    },
    [CREATE_NEW_ENGINE_BUILD_FAILURE](state) {
      return {
        ...state,
        createBuildForm: { ...state.createBuildForm, loading: false },
      };
    },
  })
);

export default reducer;

function local(state) {
  return state[namespace];
}

export const listBuilds = (
  engineId,
  { offset = 0, limit = 10 } = {},
  { force } = {}
) => (dispatch, getState) => {
  const query = `
    ${engineBuildFieldsFragment}
    query Engines (
      $engineId: ID!
      $offset: Int
      $limit: Int
    ) {
      engine (id: $engineId) {
        totalBuilds: builds (
          limit: 99999
        ) {
          count
        }
        builds (
          offset: $offset
          limit: $limit
        ) {
          count
          records {
            ...engineBuildFields
          }
        }
      }
    }
  `;

  return callGraphQLApi({
    actionTypes: [LIST, LIST_SUCCESS, LIST_FAILURE],
    query,
    variables: {
      offset,
      limit,
      engineId,
    },
    bailout: (state) => {
      if (force) {
        return false;
      }

      const builds = getPagedBuildsForEngine(state, engineId);
      const needStart = offset;
      const needEnd = offset + limit - 1;

      // bail if all needed builds exist
      return every(range(needStart, needEnd + 1), (index) => {
        return !!builds[index];
      });
    },
    dispatch,
    getState,
  });
};

export const validActions = [
  'edit-flow',
  'pause',
  'unpause',
  'submit',
  'deploy',
  'approve',
  'disapprove',
  'delete',
];

export const doBuildAction = (action, { engineId, id }) => (
  dispatch,
  getState
) => {
  const query = `
  ${engineBuildFieldsFragment}
    mutation updateEngineBuild($input: UpdateBuild!) {
      updateEngineBuild(input: $input) {
        ...engineBuildFields
      }
    }
  `;

  return callGraphQLApi({
    actionTypes: [BUILD_ACTION, BUILD_ACTION_SUCCESS, BUILD_ACTION_FAILURE],
    query,
    variables: {
      input: {
        engineId,
        action,
        id,
      },
    },
    dispatch,
    getState,
  });
};

export const confirmDoBuildAction = withConfirmation(
  doBuildAction,
  (type, { id }) => ({
    message: `Are you sure you want to ${type} build "${id}"?`,
    confirmButtonLabel: capitalize(type),
    cancelButtonLabel: 'Cancel',
  })
);

export const deleteBuild = ({ engineId, id }) => (dispatch, getState) => {
  const query = `
    mutation deleteEngineBuild($input: DeleteBuild!) {
      deleteEngineBuild(input: $input) {
        id
        message
      }
    }
  `;

  return callGraphQLApi({
    actionTypes: [DELETE, DELETE_SUCCESS, DELETE_FAILURE],
    query,
    variables: {
      input: {
        engineId,
        id,
      },
    },
    dispatch,
    getState,
  });
};

export const confirmDeleteBuild = withConfirmation(deleteBuild, ({ id }) => ({
  message: `Are you sure you want to delete the build "${id}"?
      You won't be able to undo this action`,
  confirmButtonLabel: 'Delete',
  cancelButtonLabel: 'Cancel',
}));

// Build Report data available on the GraphQL response now,
// This may not be needed anymore
export const downloadBuildReport = (report) => ({
  type: DOWNLOAD_BUILD_REPORT,
  payload: report,
});

export const buildNodeRedEngine = (payload) => ({
  type: BUILD_NODE_RED_ENGINE,
  payload,
});

export const redirectToAutomateWithBuild = (payload) => ({
  type: REDIRECT_LATEST_AUTOMATE_BUILD,
  payload: payload,
});

// (state, engineId) => { 0: { ...build }, 1: { ...build } }
export const getPagedBuildsForEngine = createCachedSelector(
  (state, engineId) =>
    makeSelectors(engineId).selectPaginationOrder(local(state)),
  (state, engineId) => local(state).buildsById,
  (buildOrder, buildsById, ...args) =>
    mapValues(buildOrder, (id) => buildsById[id])
)((state, engineId) => engineId);

export function getBuilds(state) {
  return local(state).buildsById;
}

export function getCreateBuildForm(state) {
  return local(state).createBuildForm;
}

export function getListBuildsStatus(state, engineId) {
  return makeSelectors(engineId).selectFetchingStatus(local(state));
}

export function getLoadingBuilds(state, engineId) {
  return makeSelectors(engineId).selectFetchingEntities(local(state));
}

export function getFailedLoadingBuilds(state, engineId) {
  return makeSelectors(engineId).selectFailedFetchingEntities(local(state));
}

export function getTotalBuilds(state, engineId) {
  return makeSelectors(engineId).selectTotalEntities(local(state));
}

export const updateWorkflowData = ({ organizationId, engineFlow }) => (
  dispatch,
  getState
) => {
  const query = `
    mutation setWorkflowRuntimeStorageData(
      $workflowRuntimeId: ID!,
      $input: CreateWorkflowRuntimeStorageData!
      ) {
      result: setWorkflowRuntimeStorageData(
        workflowRuntimeId: $workflowRuntimeId, input: $input
        ) {
        storageKey
      }
    }
  `;
  return callGraphQLApi({
    actionTypes: [
      UPDATE_WORLFLOW_DATA,
      UPDATE_WORLFLOW_DATA_SUCCESS,
      UPDATE_WORLFLOW_DATA_FAILURE,
    ],
    query,
    variables: {
      workflowRuntimeId: `nr${organizationId}`,
      input: engineFlow,
    },
    bailout: undefined,
    dispatch,
    getState,
  });
};

export const fetchWorkflowRuntimeData = ({ engineId, organizationId }) => (
  dispatch,
  getState
) => {
  // get the workflow runtime storage data for build nodered engine
  const query = `
    query {
      workflowRuntimeStorageData(workflowRuntimeId: "nr${organizationId}") {
        records{
          storageKey
          storageData
          storageMetadata
        }
      }
    }`;

  return callGraphQLApi({
    actionTypes: [
      FETCH_WORKFLOW_RUNTIME_DATA,
      FETCH_WORKFLOW_RUNTIME_DATA_SUCCESS,
      FETCH_WORKFLOW_RUNTIME_DATA_FAILURE,
    ],
    query,
    variables: {
      engineId,
      organizationId,
    },
    bailout: undefined,
    dispatch,
    getState,
  });
};

const engineBuildFieldsFragment = `
  fragment engineBuildFields on Build {
    id
    name
    status
    manifest
    createdBy
    validateUri
    dockerImage
    outputFormats
    createdDateTime
    modifiedDateTime
    preferredInputFormat
    supportedInputFormats
    modifiedBy
    price
    executeUri
    description
    supportedSourceTypes
    engineId
    report
    runtime
    version
    primaryAction
    secondaryActions
    validStateActions
  }
`;

export const getEngineBuildsQuery = `
  query engineBuilds($engineId: ID!){
    engine(id: $engineId){
      builds{
        records{
          version
          id
          name
          status
        }
      }
    }
  }
`;

export const getEngineQuery = `
  query($engineId: ID!){
    engine(id:$engineId){
      name
      id
      manifest
    }
  }
`;

export const createEngineBuildAndUpdateEngineJWTRightsMutation = `
  mutation($engineId: ID!, $manifest: JSONData, $taskRuntime: JSONData,
    $dockerImage: String, $JWTRights:JWTRightsField){
    createEngineBuild(input: {
      engineId: $engineId
      manifest: $manifest
      taskRuntime: $taskRuntime
      dockerImage: $dockerImage
    }){
      engineId
      id
    }
    updateEngine(input: {
      id: $engineId
      jwtRights: $JWTRights
    }){
      name
      id
      jwtRights
    }
  }
`;

export const automateEngineJWTRights = {
  roles: [
    {
      roleName: 'workflow',
      taskRights: [
        'ingestion:create',
        'ingestion:read',
        'ingestion:update',
        'ingestion:delete',
        'job:create',
        'job:read',
        'job:update',
        'job:delete',
        'recording:create',
        'recording:read',
        'recording:update',
        'recording:delete',
        'mentions:create',
        'mentions:read',
        'mentions:update',
        'mentions:delete',
        'collection:create',
        'collection:read',
        'collection:update',
        'collection:delete',
        'asset:uri',
        'asset:all',
        'task:update',
        'report:create',
        'analytics:usage',
      ],
      assetRights: ['recording:update'],
    },
  ],
};

export const createNewEngineBuild = ({ engineId, dockerImageUrl }) => (
  dispatch,
  getState
) => {
  const query = `mutation createEngineBuild ($engineId: ID!, $dockerUrl: String!){
    createEngineBuild(
      input: {
        engineId: $engineId
        dockerImage: $dockerUrl
      }
    ) {
      id
      engineId
    }
  }`;
  return callGraphQLApi({
    actionTypes: [
      CREATE_NEW_ENGINE_BUILD,
      CREATE_NEW_ENGINE_BUILD_SUCCESS,
      CREATE_NEW_ENGINE_BUILD_FAILURE,
    ],
    query,
    variables: {
      engineId,
      dockerUrl: dockerImageUrl,
    },
    bailout: undefined,
    dispatch,
    getState,
  });
};

// TODO: should we rename to createNodeRedEngineBuild
export const createEngineBuild = (
  engineId,
  flows,
  packageObj,
  taskRuntime,
  dockerImage
) => {
  const query = `
  mutation($input: CreateBuild!){
    createEngineBuild(input: $input){
      engineId
      id
    }
  }
`;
  return {
    [CALL_API]: {
      types: [BUILD, BUILD_SUCCESS, BUILD_FAILURE],
      endpoint: (state) => getConfig(state).services.graphqlUri,
      method: 'POST',
      body: JSON.stringify({
        query,
        variables: {
          input: {
            engineId: engineId,
            manifest: {
              runtime: 'nodeRed',
              engineId: engineId,
              engineMode: 'chunk',
            },
            taskRuntime: {
              edge: {},
              nodeRed: {
                flows: flows,
                package: packageObj || {},
              },
            },
          },
        },
      }),
      headers: commonHeaders,
    },
  };
};



// WEBPACK FOOTER //
// ./src/client/modules/engine-builds/index.js